import {
  DATE_FORMATTER_DAYS,
  DATE_FORMATTER_HOURS_MINUTES_ONLY,
  DATE_FORMATTER_HOURS_ONLY,
  DATE_FORMATTER_MINUTES,
  DATE_FORMATTER_MONTH_DAY_ONLY,
} from "utils/constants";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { T } from "services/lang/LanguageService";
import { Instant, IsoFields, ZonedDateTime, ZoneId } from "@js-joda/core";
import { toZonedDateTime } from "utils/time-utils";
import { AggregationPeriod, DataDisplayType, DiffType } from "./ValueChangeIndicatorTypes";

export const getMainClassName = (dataDisplayType: DataDisplayType, reportMode?: boolean, displayInline?: boolean) => {
  const currentClassName = `last-value-indicator${reportMode ? "-report-mode" : ""}`;

  if (dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART) {
    return `${currentClassName}-horizontal`;
  }
  if (displayInline) {
    return `${currentClassName}-inline`;
  }
  return currentClassName;
};

export const getTitleClassName = (dataDisplayType: DataDisplayType, displayInline?: boolean): string => {
  const baseTitle = dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "title-horizontal" : "title";

  const fontWeight = displayInline || dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "font-bold" : "font-medium";

  return `${baseTitle} ${fontWeight}`;
};

export const getDiffClassName = (
  isNeutral: boolean,
  dataDisplayType: DataDisplayType,
  useDiffIndicatorColors?: boolean
): string => {
  const baseDiff = dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "last-value-diff-horizontal" : "last-value-diff";

  const classNames = [
    baseDiff,
    "flex flex-row align-items-center justify-content-end",
    useDiffIndicatorColors && !isNeutral && "indicator-colors",
    isNeutral && "neutral",
  ]
    .filter(Boolean)
    .join(" ");

  return classNames;
};

export const getPeriodLabel = (
  Unit: AggregationPeriod,
  referenceTime: Instant | undefined,
  zoneId: ZoneId,
  lastFinishedWeek?: boolean
) => {
  const currentAlignUnit = (Unit ?? AggregationPeriod.MONTHS).toString().toUpperCase();
  const now = toZonedDateTime(referenceTime || Instant.now(), zoneId);

  switch (currentAlignUnit) {
    case AggregationPeriod.MINUTES:
      return now.format(DATE_FORMATTER_HOURS_MINUTES_ONLY);
    case AggregationPeriod.HOURS:
      return now.format(DATE_FORMATTER_HOURS_ONLY);
    case AggregationPeriod.DAYS:
      return now.format(DATE_FORMATTER_DAYS);
    case AggregationPeriod.WEEKS: {
      const week = now.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      if (lastFinishedWeek) {
        return `W${week - 1}`;
      }
      return `W${week}`;
    }
    case AggregationPeriod.MONTHS: {
      const lastDayOfMonth = now.withDayOfMonth(1).minusDays(1);
      return lastDayOfMonth.format(DATE_FORMATTER_MONTH_DAY_ONLY);
    }
    case AggregationPeriod.YEARS:
      return String(now.year());
    default:
      return now.format(DATE_FORMATTER_MINUTES);
  }
};

export const getMonthYearComparison = (dataset: InstantDoubleValue[], isLastFinishedMonth: boolean, zone: ZoneId) => {
  const now = ZonedDateTime.now(zone);

  const firstDayOfCurrentMonth = now.withDayOfMonth(1).withHour(0).withMinute(0).withSecond(0).withNano(0);

  let lastFinished: ZonedDateTime;

  if (isLastFinishedMonth) {
    lastFinished = firstDayOfCurrentMonth.minusNanos(1);
  } else {
    lastFinished = firstDayOfCurrentMonth;
  }

  const targetYear = lastFinished.year();
  const targetMonth = lastFinished.monthValue();

  const findValue = (y: number, m: number) => {
    return (
      dataset.find(d => {
        const dt = ZonedDateTime.parse(d.key).withZoneSameInstant(zone);
        return dt.year() === y && dt.monthValue() === m;
      })?.value ?? null
    );
  };

  const curr = findValue(targetYear, targetMonth);
  const prev = findValue(targetYear - 1, targetMonth);
  const currValueKey = `${targetYear}-${targetMonth}`;
  const prevValueKey = `${targetYear - 1}-${targetMonth}`;

  return { curr, prev, currValueKey, prevValueKey };
};

export const getNoDataMessageWeek = (upToLastFinishedWeek?: boolean) =>
  upToLastFinishedWeek ? T("label.no-data-last-finished-week") : T("label.no-data-current-week");

export const getDiffClass = (isNegative: boolean) => (isNegative ? "negative" : "positive");

export const getArrowDirection = (isNegative: boolean) => (isNegative ? "down" : "up");

export const formatDiff = (
  prev: number,
  curr: number,
  unit?: string,
  value?: number,
  isNeutral?: boolean,
  diffType?: DiffType,
  decimalPlaces?: number,
  dataDisplayType?: DataDisplayType
) => {
  if (!value) return "0";
  if (dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART) {
    if (isNeutral) {
      return "0";
    }

    if (diffType === DiffType.PERCENTAGE) {
      const percent = ((curr - prev) / prev) * 100;
      return `${percent.toFixed(decimalPlaces)}%`;
    }
    return `${(curr - prev).toFixed(decimalPlaces)} ${unit}`;
  }
  return `${value.toFixed(decimalPlaces)} ${diffType === DiffType.PERCENTAGE ? "%" : unit}`;
};
